import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerProps,
  FormControl,
  FormLabel,
  Stack,
  useToast,
} from '@chakra-ui/react';
import { usePutUomsMutation } from 'api';
import { PutUnitOfMeasureRequest } from 'api/bindings/PutUnitOfMeasureRequest';
import { UnitOfMeasureResponse } from 'api/bindings/UnitOfMeasureResponse';
import { AppInput, FormButtonGroup } from 'features';
import { useCallback } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

export interface EditUnitOfMeasurementDrawerProps
  extends Omit<DrawerProps, 'onSubmit' | 'children' | 'isOpen'> {
  uom: UnitOfMeasureResponse;
  disallowedUomNames?: string[];
}

interface Form extends PutUnitOfMeasureRequest {
  id: string;
}

export const EditUnitOfMeasurementDrawer = (
  props: EditUnitOfMeasurementDrawerProps
) => {
  const { uom, onClose, disallowedUomNames = [], ...rest } = props;

  // Fetches
  const [putUom] = usePutUomsMutation();

  // States
  const toast = useToast();
  const form = useForm<Form>({
    mode: 'all',
    defaultValues: uom,
  });
  const { handleSubmit, reset, control, watch } = form;
  const name = watch('name');

  const isNameValid = useCallback(
    (name) => {
      return (
        (disallowedUomNames.some(
          (x) => x.toLowerCase() === name.toLowerCase()
        ) &&
          `Unit of measurement ${name} is not available`) ||
        undefined
      );
    },
    [disallowedUomNames]
  );

  const onSubmit = useCallback(
    async (form: Form) => {
      try {
        const result = await putUom(form);
        if ('error' in result) {
          toast({
            title: `Failed to update unit of measure ${form.name}`,
            status: 'error',
          });
          return;
        }

        toast({
          title: `Unit of measure ${form.name} posted successfully!`,
          status: 'success',
        });
        reset();
        onClose();
      } catch {}
    },
    [onClose, putUom, reset, toast]
  );

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Drawer size="xl" isOpen={true} onClose={onClose}>
          <DrawerOverlay />
          <DrawerCloseButton />
          <DrawerContent>
            <DrawerHeader>Edit Unit of Measure {name}</DrawerHeader>
            <DrawerBody>
              <Stack spacing={4}>
                <FormControl>
                  <FormLabel htmlFor="name">Name:</FormLabel>
                  <Controller
                    control={control}
                    name="name"
                    rules={{
                      minLength: {
                        value: 3,
                        message: 'Name must be at least 3 characters.',
                      },
                      maxLength: {
                        value: 256,
                        message: 'Name must be at most 256 characters.',
                      },
                      validate: { isNameValid },
                    }}
                    render={(f) => <AppInput id="name" {...(f.field as any)} />}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="abbrev">Abbreviation:</FormLabel>
                  <Controller
                    control={control}
                    name="abbreviation"
                    rules={{
                      minLength: {
                        value: 1,
                        message: 'Abbreviation must be at least 1 character.',
                      },
                      maxLength: {
                        value: 64,
                        message: 'Abbreviation must at most 64 characters.',
                      },
                    }}
                    render={(f) => (
                      <AppInput name="abbrev" {...(f.field as any)} />
                    )}
                  />
                </FormControl>
              </Stack>
            </DrawerBody>
            <DrawerFooter>
              <FormButtonGroup
                onConfirm={handleSubmit(onSubmit)}
                onCancel={() => {
                  reset();
                  onClose();
                }}
              />
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      </form>
    </FormProvider>
  );
};
